import { useState, type ReactNode } from 'react'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import { BrandMark } from '@/components/layout/BrandMark'
import { ExpertComingSoonModal } from '@/components/layout/ExpertComingSoonModal'
import { PitchDeckComingSoonModal } from '@/components/layout/PitchDeckComingSoonModal'
import { cn } from '@/lib/cn'
import {
  EXPLORE_MORE_HREF,
  FOOTER_EXPLORE_CATEGORIES,
  exploreIndustryHref,
  resetExplorePageScroll,
  shortIndustryLabel,
} from '@/lib/exploreIndustryCategories'

const SOCIAL_LINKS = [
  {
    id: 'twitter',
    label: 'Vaagon on X',
    href: 'https://x.com/vaagon',
    Icon: XIcon,
  },
  {
    id: 'linkedin',
    label: 'Vaagon on LinkedIn',
    href: 'https://www.linkedin.com/company/vaagon',
    Icon: LinkedInIcon,
  },
  {
    id: 'instagram',
    label: 'Vaagon on Instagram',
    href: 'https://www.instagram.com/p/DZb_Q67jL14/',
    Icon: InstagramIcon,
  },
] as const

const linkClass =
  'text-[var(--foreground-muted)] transition hover:text-[var(--foreground)] focus-visible:text-[var(--foreground)] focus-visible:outline-none'

const columnHeadingClass =
  'text-[12px] font-semibold tracking-[0.14em] text-[var(--foreground)] uppercase'

function ColumnHeading({ children }: { children: ReactNode }) {
  return <h3 className={cn(columnHeadingClass, 'mb-3.5')}>{children}</h3>
}

function FooterExploreLink({
  href,
  children,
}: {
  href: string
  children: ReactNode
}) {
  const navigate = useNavigate()
  const location = useLocation()

  return (
    <a
      href={href}
      className={linkClass}
      onClick={(e) => {
        e.preventDefault()
        const url = new URL(href, window.location.origin)
        const target = `${url.pathname}${url.search}`
        const current = `${location.pathname}${location.search}`

        if (current === target) {
          resetExplorePageScroll()
          return
        }

        navigate({
          pathname: url.pathname,
          search: url.search,
          hash: '',
        })
        resetExplorePageScroll()
      }}
    >
      {children}
    </a>
  )
}

function FooterLinkList({
  items,
  ariaLabel,
}: {
  ariaLabel: string
  items: Array<
    | { label: string; to: string }
    | { label: string; onClick: () => void }
    | { label: string; muted: true }
  >
}) {
  return (
    <nav aria-label={ariaLabel}>
      <ul className="flex flex-col gap-2.5 text-[13px]">
        {items.map((item) => (
          <li key={item.label}>
            {'to' in item ? (
              <Link to={item.to} className={linkClass}>
                {item.label}
              </Link>
            ) : 'onClick' in item ? (
              <button
                type="button"
                onClick={item.onClick}
                className={cn(linkClass, 'text-left')}
              >
                {item.label}
              </button>
            ) : (
              <span className="cursor-default text-[var(--foreground-muted)]">
                {item.label}
              </span>
            )}
          </li>
        ))}
      </ul>
    </nav>
  )
}

export const Footer = () => {
  const [pitchOpen, setPitchOpen] = useState(false)
  const [expertOpen, setExpertOpen] = useState(false)

  return (
    <footer className="border-t border-[var(--glass-border)] bg-[var(--background)] px-4 pt-12 pb-6 sm:px-6 sm:pt-14 lg:px-8">
      <div className="mx-auto w-full max-w-[1280px]">
        <div className="grid grid-cols-2 gap-x-6 gap-y-10 sm:grid-cols-3 lg:grid-cols-6 lg:gap-x-8">
          <div className="col-span-2 sm:col-span-3 lg:col-span-1">
            <BrandMark />
            <p className="mt-4 text-[15px] leading-snug text-[var(--foreground)]">
              Startups In Motion
            </p>
            <div className="mt-5 flex items-center gap-4">
              {SOCIAL_LINKS.map(({ id, label, href, Icon }) => (
                <a
                  key={id}
                  href={href}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={label}
                  className="text-[var(--foreground-muted)] transition hover:text-[var(--foreground)] focus-visible:text-[var(--foreground)] focus-visible:outline-none"
                >
                  <Icon className="h-[18px] w-[18px]" />
                </a>
              ))}
            </div>
          </div>

          <div>
            <ColumnHeading>Platform</ColumnHeading>
            <FooterLinkList
              ariaLabel="Platform"
              items={[
                { label: 'Submit An Idea', to: '/submit-idea' },
                { label: 'Validate An Idea', to: '/validate-ideas' },
                { label: 'Dashboard', to: '/dashboard' },
                { label: 'Leaderboard', to: '/leaderboard' },
                {
                  label: 'PitchOS',
                  onClick: () => setPitchOpen(true),
                },
                {
                  label: 'Become An Expert',
                  onClick: () => setExpertOpen(true),
                },
              ]}
            />
          </div>

          <div>
            <ColumnHeading>Categories</ColumnHeading>
            <nav aria-label="Categories">
              <ul className="flex flex-col gap-2.5 text-[13px]">
                {FOOTER_EXPLORE_CATEGORIES.map(({ industry }) => (
                  <li key={industry}>
                    <FooterExploreLink href={exploreIndustryHref(industry)}>
                      {shortIndustryLabel(industry)}
                    </FooterExploreLink>
                  </li>
                ))}
                <li>
                  <FooterExploreLink href={EXPLORE_MORE_HREF}>
                    Explore More
                  </FooterExploreLink>
                </li>
              </ul>
            </nav>
          </div>

          <div>
            <ColumnHeading>Company</ColumnHeading>
            <FooterLinkList
              ariaLabel="Company"
              items={[
                { label: 'About Us', to: '/about' },
                { label: 'Help Center', to: '/help-center' },
                { label: 'Testimonials', to: '/#testimonials-heading' },
              ]}
            />
          </div>

          <div>
            <ColumnHeading>Resources</ColumnHeading>
            <FooterLinkList
              ariaLabel="Resources"
              items={[
                { label: 'By Startup Stage', muted: true },
                { label: 'By User', muted: true },
                { label: 'Learning Path', muted: true },
              ]}
            />
          </div>

          <div>
            <ColumnHeading>Legal</ColumnHeading>
            <FooterLinkList
              ariaLabel="Legal"
              items={[
                { label: 'Privacy Policy', to: '/privacy' },
                { label: 'Terms of Service', to: '/terms' },
                { label: 'Cookie Policy', to: '/cookies' },
              ]}
            />
          </div>
        </div>

        <div className="mt-12 border-t border-[var(--glass-border)] pt-5">
          <p className="text-center text-[12px] text-[var(--foreground-muted)]">
            © 2026 JOMS Commerce And Technologies Private Limited. All rights
            reserved.
          </p>
        </div>
      </div>

      <PitchDeckComingSoonModal
        open={pitchOpen}
        onClose={() => setPitchOpen(false)}
      />
      <ExpertComingSoonModal
        open={expertOpen}
        onClose={() => setExpertOpen(false)}
      />
    </footer>
  )
}

function XIcon({ className }: { className?: string }) {
  return (
    <svg
      aria-hidden
      className={className}
      viewBox="0 0 15 15"
      fill="currentColor"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path d="M11.8125 0.703125H14.1129L9.08786 6.46098L15 14.2974H10.3714L6.74357 9.54563L2.59714 14.2974H0.294643L5.66893 8.1367L0 0.704196H4.74643L8.02071 5.0467L11.8125 0.703125ZM11.0036 12.9174H12.2786L4.05 2.01134H2.68286L11.0036 12.9174Z" />
    </svg>
  )
}

function LinkedInIcon({ className }: { className?: string }) {
  return (
    <svg
      aria-hidden
      className={className}
      viewBox="0 0 20 20"
      fill="currentColor"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path d="M15.8333 2.5C16.2754 2.5 16.6993 2.67559 17.0118 2.98816C17.3244 3.30072 17.5 3.72464 17.5 4.16667V15.8333C17.5 16.2754 17.3244 16.6993 17.0118 17.0118C16.6993 17.3244 16.2754 17.5 15.8333 17.5H4.16667C3.72464 17.5 3.30072 17.3244 2.98816 17.0118C2.67559 16.6993 2.5 16.2754 2.5 15.8333V4.16667C2.5 3.72464 2.67559 3.30072 2.98816 2.98816C3.30072 2.67559 3.72464 2.5 4.16667 2.5H15.8333ZM15.4167 15.4167V11C15.4167 10.2795 15.1304 9.5885 14.621 9.07903C14.1115 8.56955 13.4205 8.28333 12.7 8.28333C11.9917 8.28333 11.1667 8.71667 10.7667 9.36667V8.44167H8.44167V15.4167H10.7667V11.3083C10.7667 10.6667 11.2833 10.1417 11.925 10.1417C12.2344 10.1417 12.5312 10.2646 12.75 10.4834C12.9688 10.7022 13.0917 10.9989 13.0917 11.3083V15.4167H15.4167ZM5.73333 7.13333C6.10464 7.13333 6.46073 6.98583 6.72328 6.72328C6.98583 6.46073 7.13333 6.10464 7.13333 5.73333C7.13333 4.95833 6.50833 4.325 5.73333 4.325C5.35982 4.325 5.0016 4.47338 4.73749 4.73749C4.47338 5.0016 4.325 5.35982 4.325 5.73333C4.325 6.50833 4.95833 7.13333 5.73333 7.13333ZM6.89167 15.4167V8.44167H4.58333V15.4167H6.89167Z" />
    </svg>
  )
}

function InstagramIcon({ className }: { className?: string }) {
  return (
    <svg
      aria-hidden
      className={className}
      viewBox="0 0 20 20"
      fill="currentColor"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path d="M10.8545 1.66406C11.792 1.66656 12.2679 1.67156 12.6787 1.68323L12.8404 1.68906C13.027 1.69573 13.2112 1.70406 13.4337 1.71406C14.3204 1.75573 14.9254 1.89573 15.4562 2.10156C16.0062 2.31323 16.4695 2.5999 16.9329 3.0624C17.3568 3.47884 17.6847 3.98279 17.8937 4.53906C18.0995 5.0699 18.2395 5.6749 18.2812 6.5624C18.2912 6.78406 18.2995 6.96823 18.3062 7.15573L18.3112 7.3174C18.3237 7.7274 18.3287 8.20323 18.3304 9.14073L18.3312 9.7624V10.8541C18.3332 11.4619 18.3268 12.0697 18.312 12.6774L18.307 12.8391C18.3004 13.0266 18.292 13.2107 18.282 13.4324C18.2404 14.3199 18.0987 14.9241 17.8937 15.4557C17.6847 16.012 17.3568 16.516 16.9329 16.9324C16.5164 17.3563 16.0125 17.6842 15.4562 17.8932C14.9254 18.0991 14.3204 18.2391 13.4337 18.2807L12.8404 18.3057L12.6787 18.3107C12.2679 18.3224 11.792 18.3282 10.8545 18.3299L10.2329 18.3307H9.14204C8.53392 18.3329 7.92581 18.3265 7.31787 18.3116L7.1562 18.3066C6.95838 18.2991 6.7606 18.2905 6.56287 18.2807C5.6762 18.2391 5.0712 18.0991 4.53954 17.8932C3.98356 17.6841 3.47991 17.3562 3.0637 16.9324C2.63949 16.516 2.31129 16.0121 2.10204 15.4557C1.8962 14.9249 1.7562 14.3199 1.71454 13.4324L1.68954 12.8391L1.68537 12.6774C1.67001 12.0697 1.66306 11.4619 1.66454 10.8541V9.14073C1.66223 8.5329 1.66834 7.92506 1.68287 7.3174L1.6887 7.15573C1.69537 6.96823 1.7037 6.78406 1.7137 6.5624C1.75537 5.6749 1.89537 5.07073 2.1012 4.53906C2.31095 3.98256 2.63972 3.47859 3.06454 3.0624C3.4805 2.63869 3.98386 2.31079 4.53954 2.10156C5.0712 1.89573 5.67537 1.75573 6.56287 1.71406C6.78454 1.70406 6.96954 1.69573 7.1562 1.68906L7.31787 1.68406C7.92553 1.66926 8.53337 1.66287 9.1412 1.6649L10.8545 1.66406ZM9.99787 5.83073C8.8928 5.83073 7.83299 6.26972 7.05159 7.05112C6.27019 7.83252 5.8312 8.89233 5.8312 9.9974C5.8312 11.1025 6.27019 12.1623 7.05159 12.9437C7.83299 13.7251 8.8928 14.1641 9.99787 14.1641C11.1029 14.1641 12.1627 13.7251 12.9441 12.9437C13.7255 12.1623 14.1645 11.1025 14.1645 9.9974C14.1645 8.89233 13.7255 7.83252 12.9441 7.05112C12.1627 6.26972 11.1029 5.83073 9.99787 5.83073ZM9.99787 7.4974C10.3262 7.49734 10.6513 7.56195 10.9546 7.68754C11.2579 7.81312 11.5336 7.99723 11.7658 8.22933C11.9979 8.46144 12.1821 8.73701 12.3078 9.0403C12.4335 9.3436 12.4982 9.66867 12.4983 9.99698C12.4983 10.3253 12.4337 10.6504 12.3081 10.9537C12.1826 11.2571 11.9985 11.5327 11.7663 11.7649C11.5342 11.9971 11.2587 12.1812 10.9554 12.3069C10.6521 12.4326 10.327 12.4973 9.9987 12.4974C9.33566 12.4974 8.69978 12.234 8.23094 11.7652C7.76209 11.2963 7.4987 10.6604 7.4987 9.9974C7.4987 9.33435 7.76209 8.69847 8.23094 8.22963C8.69978 7.76079 9.33566 7.4974 9.9987 7.4974M14.3737 4.58073C14.0974 4.58073 13.8325 4.69048 13.6371 4.88583C13.4418 5.08118 13.332 5.34613 13.332 5.6224C13.332 5.89866 13.4418 6.16361 13.6371 6.35897C13.8325 6.55432 14.0974 6.66406 14.3737 6.66406C14.65 6.66406 14.9149 6.55432 15.1103 6.35897C15.3056 6.16361 15.4154 5.89866 15.4154 5.6224C15.4154 5.34613 15.3056 5.08118 15.1103 4.88583C14.9149 4.69048 14.65 4.58073 14.3737 4.58073Z" />
    </svg>
  )
}
